import { PermissionEnum } from '@Application/Security/Roles';
import RetryWorkflowStep from '@Application/WorkflowRuns/Retry/Commands/RetryWorkflowStep';
import retryWorkflowStep from '@Application/WorkflowRuns/Retry/retryWorkflowStep';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useSubjectModifiability from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Hooks/useSubjectModifiability';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type TranscriptionIssueView from '@UI/Store/WorkflowRuns/Views/TranscriptionIssueView';
import { RotateCcw } from 'lucide-react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const RETRY_PERMISSIONS = [PermissionEnum.INTERVIEW_WRITE, PermissionEnum.DOCUMENT_GENERATION_LAUNCH];

type Props = {
	readonly subject: SubjectAddress;
	readonly issue: TranscriptionIssueView;
};

/**
 * Offered to a reader allowed to launch a transcription, on a dossier that still accepts writes.
 * The parent checks `issue.canRetry`: the server decides whether a retry makes sense. The toast
 * comes from `StepRetryNotifications`.
 */
const RetryTranscriptionButton: React.FC<Props> = ({ subject, issue }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const { canModify } = useSubjectModifiability(subject);
	const [isRetrying, setIsRetrying] = React.useState(false);

	const handleRetry = React.useCallback(() => {
		setIsRetrying(true);

		dispatch(
			retryWorkflowStep(
				new RetryWorkflowStep(currentFacility.id, subject, issue.runId, issue.stepRunId),
			),
		)
			.catch(ignoreHandledError)
			.finally(() => setIsRetrying(false));
	}, [dispatch, currentFacility.id, subject, issue.runId, issue.stepRunId]);

	return (
		<ConditionalLoader data={canModify}>
			<WithPermission permission={RETRY_PERMISSIONS} mode="any">
				<Button
					type="button"
					severity="secondary"
					label="Relancer"
					icon={<RotateCcw size={16} />}
					loading={isRetrying}
					onClick={handleRetry}
				/>
			</WithPermission>
		</ConditionalLoader>
	);
};

export default RetryTranscriptionButton;
